import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, BookOpen, Users, Globe, Newspaper, Mail, FileText, ChevronUp, ChevronDown, RotateCcw, UserCog, Inbox, DatabaseBackup, Ticket, Percent, MailPlus, BarChart3, Link2, Award, Crown } from "lucide-react";
import { api, euro, fmtDateTime, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ContentEditor from "@/pages/admin/ContentEditor";
import NewsManager from "@/pages/admin/NewsManager";
import Contacts from "@/pages/admin/Contacts";
import CourseManager from "@/pages/admin/CourseManager";
import Invoices from "@/pages/admin/Invoices";
import TrainerManager from "@/pages/admin/TrainerManager";
import EmailLog from "@/pages/admin/EmailLog";
import EmailTemplates from "@/pages/admin/EmailTemplates";
import Analytics from "@/pages/admin/Analytics";
import LinkManager from "@/pages/admin/LinkManager";
import BackupManager from "@/pages/admin/BackupManager";
import VoucherManager from "@/pages/admin/VoucherManager";
import DiscountManager from "@/pages/admin/DiscountManager";
import NewsletterManager from "@/pages/admin/NewsletterManager";
import Mailbox from "@/pages/admin/Mailbox";
import Platzreife from "@/pages/admin/Platzreife";
import ManualVouchers from "@/pages/admin/ManualVouchers";
import PriceListManager from "@/pages/admin/PriceListManager";
import TrainingsclubMembers from "@/pages/admin/TrainingsclubMembers";
import { ManualPaymentAction } from "./ManualPaymentAction";

const Admin = () => {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [selectedTab, setSelectedTab] = useState("dashboard");
  const tab = pathname === "/admin/preisliste" ? "pricelist" : selectedTab;
  const setTab = (id) => { if (id !== "pricelist") setSelectedTab(id); nav(id === "pricelist" ? "/admin/preisliste" : "/admin/dashboard"); };
  const [mailUnread, setMailUnread] = useState(0);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const loadUnread = () => api.get("/admin/mailbox/summary").then((r) => {
      const inbox = (r.data.folders || []).find((f) => f.key === "inbox");
      setMailUnread(inbox?.unread || 0);
    }).catch(() => {});
    loadUnread();
    const iv = setInterval(loadUnread, 60000);
    return () => clearInterval(iv);
  }, [user]);

  useEffect(() => { if (ready && !user) nav("/admin"); }, [ready, user, nav]);
  if (!ready || !user) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-neutral-500 font-mono-lab">Lädt…</div>;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 bg-[#050505]/95 backdrop-blur z-10">
        <div className="min-w-0 pr-3">
          <span className="font-heading text-sm sm:text-xl uppercase text-white break-words">GOLFCOACHING<span className="text-[#ff0033]">.</span>ACADEMY</span>
          <span className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase block sm:inline sm:ml-3">Admin · {user.role}</span>
        </div>
        <button onClick={() => { logout(); nav("/admin"); }} data-testid="logout-btn" className="flex items-center gap-2 text-neutral-400 hover:text-[#ff0033] font-mono-lab text-xs uppercase tracking-widest"><LogOut size={16} /> Logout</button>
      </header>

      <div className="flex">
        <nav className="w-16 md:w-56 shrink-0 border-r border-white/10 min-h-[calc(100vh-65px)] p-3 md:p-4 space-y-1">
          {user.role === "admin" && <button onClick={() => setTab("pricelist")} data-testid="admin-tab-pricelist" title="Preisliste" className={`w-full flex items-center gap-3 px-3 py-3 font-mono-lab text-xs uppercase tracking-widest ${tab === "pricelist" ? "bg-[#ff0033] text-white" : "text-neutral-400 hover:text-white"}`}><FileText size={18} className="shrink-0" /><span className="hidden md:inline">Preisliste</span></button>}
          {[["dashboard", LayoutDashboard, "Dashboard"], ["analytics", BarChart3, "Analytics"], ...(user.role === "admin" ? [["mailbox", Inbox, "E-Mail-Postfach"]] : []), ["content", Globe, "Website · Inhalte"], ["news", Newspaper, "News-Portal"], ["contacts", Mail, "Kontaktliste"], ["newsletter", MailPlus, "Newsletter-Mails"], ["courses", BookOpen, "Kurse & Termine"], ["bookings", Users, "Buchungen & Zahlungen"], ["platzreife", Award, "Platzreife"], ["trainingsclub", Crown, "Trainingsclub"], ["manualvouchers", Ticket, "Manuelle Gutscheine"], ["invoices", FileText, "Rechnungen"], ["emaillog", Inbox, "E-Mail-Protokoll"], ["emailtexts", MailPlus, "E-Mail-Texte"], ...(user.role === "admin" ? [["trainers", UserCog, "Trainerzugänge"], ["vouchers", Ticket, "Gutscheine"], ["discounts", Percent, "Rabattcodes"], ["links", Link2, "Kurzlinks & QR"], ["backups", DatabaseBackup, "Sicherung"]] : [])].map(([id, Icon, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`} className={`w-full flex items-center gap-3 px-3 py-3 font-mono-lab text-xs uppercase tracking-widest ${tab === id ? "bg-[#ff0033] text-white" : "text-neutral-400 hover:text-white"}`}>
              <Icon size={18} /> <span className="hidden md:inline">{label}</span>
              {id === "mailbox" && mailUnread > 0 && <span data-testid="mailbox-nav-badge" className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${tab === id ? "bg-black/30 text-white" : "bg-[#ff0033] text-white"}`}>{mailUnread}</span>}
            </button>
          ))}
        </nav>
        <main className="flex-1 min-w-0 p-4 md:p-8 overflow-x-auto">
          {tab === "pricelist" && user.role === "admin" && <PriceListManager />}
          {tab === "pricelist" && user.role !== "admin" && <p data-testid="price-list-access-denied" className="text-neutral-300">Die Preisliste ist nur für Administratoren verfügbar.</p>}
          {tab === "dashboard" && <Dashboard onOpenMailbox={() => setTab("mailbox")} mailUnread={user.role === "admin" ? mailUnread : 0} />}
          {tab === "mailbox" && user.role === "admin" && <Mailbox />}
          {tab === "analytics" && <Analytics />}
          {tab === "content" && <ContentEditor canReset={user.role === "admin"} />}
          {tab === "news" && <NewsManager />}
          {tab === "contacts" && <Contacts canDelete={user.role === "admin"} />}
          {tab === "newsletter" && <NewsletterManager canSend={user.role === "admin"} />}
          {tab === "courses" && <CourseManager canDelete={user.role === "admin"} />}
          {tab === "bookings" && <Bookings />}
          {tab === "platzreife" && <Platzreife canEdit={user.role === "admin" || user.role === "coach"} canEditDetails={user.role === "admin"} />}
          {tab === "trainingsclub" && <TrainingsclubMembers canManage={user.role === "admin"} />}
          {tab === "manualvouchers" && <ManualVouchers canDelete={user.role === "admin"} />}
          {tab === "invoices" && <Invoices canManage={user.role === "admin"} />}
          {tab === "emaillog" && <EmailLog canManage={user.role === "admin" || user.role === "coach"} />}
          {tab === "emailtexts" && <EmailTemplates canManage={user.role === "admin"} />}
          {tab === "trainers" && user.role === "admin" && <TrainerManager />}
          {tab === "backups" && user.role === "admin" && <BackupManager />}
          {tab === "vouchers" && user.role === "admin" && <VoucherManager />}
          {tab === "discounts" && user.role === "admin" && <DiscountManager />}
          {tab === "links" && user.role === "admin" && <LinkManager />}
        </main>
      </div>
    </div>
  );
};

const Card = ({ label, value }) => (
  <div className="bg-[#0F0F0F] border border-white/10 p-5">
    <p className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase">{label}</p>
    <p className="font-heading text-3xl text-white mt-2">{value}</p>
  </div>
);

const Dashboard = ({ onOpenMailbox, mailUnread = 0 }) => {
  const [d, setD] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = () => {
    setRefreshing(true);
    return api.get("/admin/dashboard")
      .then((r) => { setD(r.data); setUpdatedAt(new Date()); })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", load);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", load); };
  }, []);
  if (!d) return <p className="text-neutral-500 font-mono-lab">Lädt…</p>;
  return (
    <div>
      {mailUnread > 0 && (
        <button onClick={onOpenMailbox} data-testid="dashboard-mail-hint" className="w-full mb-5 border border-[#ff0033]/40 bg-[#ff0033]/10 p-3 text-left text-sm text-white hover:bg-[#ff0033]/15 transition-colors flex items-center gap-2">
          <Inbox size={15} className="text-[#ff0033]" /> {mailUnread} neue E-Mail{mailUnread > 1 ? "s" : ""} im Postfach – jetzt lesen
        </button>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-4xl uppercase tracking-tight text-white">Dashboard</h1>
        <div className="flex items-center gap-3">
          {updatedAt && <span className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase" data-testid="dashboard-updated-at">Stand {updatedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}
          <button onClick={load} disabled={refreshing} data-testid="dashboard-refresh-btn" className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2 font-mono-lab text-xs uppercase tracking-widest disabled:opacity-50">
            <RotateCcw size={14} className={refreshing ? "animate-spin" : ""} /> Aktualisieren
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card label="Umsatz heute" value={euro(d.revenue_today)} />
        <Card label="Umsatz Woche" value={euro(d.revenue_week)} />
        <Card label="Umsatz Monat" value={euro(d.revenue_month)} />
        <Card label="Umsatz gesamt" value={euro(d.revenue_total)} />
        <Card label="Buchungen" value={d.total_bookings} />
        <Card label="Stornos" value={d.cancellations} />
        <Card label="Offene Zahlungen" value={d.open_payments} />
        <Card label="Kurse heute" value={d.today_courses.length} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0F0F0F] border border-white/10 p-5">
          <p className="font-mono-lab text-xs tracking-widest text-[#ff0033] uppercase mb-4">Kommende Kurse</p>
          {d.upcoming_courses.length === 0 ? <p className="text-neutral-500 text-sm">Keine</p> : d.upcoming_courses.map((s) => (
            <div key={s.id} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span className="text-white">{s.course_name}</span>
              <span className="text-neutral-400">{fmtDateTime(s.start)} · {s.booked}/{s.capacity}</span>
            </div>
          ))}
        </div>
        <div className="bg-[#0F0F0F] border border-white/10 p-5">
          <p className="font-mono-lab text-xs tracking-widest text-[#ff0033] uppercase mb-4">Beliebteste Kurse</p>
          {d.popular_courses.map((p) => (
            <div key={p.name} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span className="text-white">{p.name}</span><span className="text-neutral-400">{p.count} Teiln.</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const payInfo = (b) => {
  if (b.status === "cancelled") return { key: "cancelled", label: "Storniert", cls: "bg-neutral-700/40 text-neutral-300 border-neutral-600" };
  const ps = b.payment_status;
  if (ps === "refunded") return { key: "refunded", label: "Rückerstattet", cls: "bg-blue-500/15 text-blue-300 border-blue-500/40" };
  if (ps === "refund_failed") return { key: "refund_failed", label: "Rückerst. fehlgeschlagen", cls: "bg-red-500/15 text-red-300 border-red-500/50" };
  if (ps === "paid") return { key: "paid", label: "Bezahlt", cls: "bg-green-500/15 text-green-400 border-green-500/40" };
  if (ps === "partial") return { key: "partial", label: "Teilweise bezahlt", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" };
  if (ps === "failed") return { key: "failed", label: "Zahlung fehlgeschlagen", cls: "bg-red-500/15 text-red-300 border-red-500/50" };
  if (ps === "payment_requested" || b.payment_request_status === "sent") return { key: "requested", label: "Aufforderung gesendet", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" };
  if (ps === "free" || ps === "comp") return { key: "free", label: "Kostenlos", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" };
  return { key: "pending", label: "Ausstehend", cls: "bg-neutral-600/30 text-neutral-300 border-neutral-500/40" };
};
const PAY_FILTERS = [["paid", "Bezahlt"], ["pending", "Ausstehend"], ["requested", "Aufforderung gesendet"], ["partial", "Teilweise bezahlt"], ["failed", "Fehlgeschlagen"], ["refunded", "Rückerstattet"], ["refund_failed", "Rückerst. fehlgeschlagen"], ["cancelled", "Storniert"], ["free", "Kostenlos"]];
const SOURCE_LABEL = { website: "Website", telephone: "Telefon", email: "E-Mail", reception: "Persönlich", external_calendar: "Ext. Kalender", manual: "Admin manuell", other: "Sonstiges" };
const isToday = (iso) => { if (!iso) return false; const d = new Date(iso); const n = new Date(); return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate(); };

const Bookings = () => {
  const [list, setList] = useState([]);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [busy, setBusy] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [f, setF] = useState({ course: "", pay: "", book: "", provider: "", source: "", from: "", to: "", q: "" });
  const load = () => api.get("/admin/bookings").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const cancelBooking = (b) => setCancelTarget(b);

  const doCancel = async (notify) => {
    const b = cancelTarget;
    if (!b) return;
    setCancelTarget(null);
    setBusy(b.id);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/cancel`, { notify });
      toast.success(data.message || (notify ? "Storniert" : "Still storniert"));
      load();
    } catch (e) {
      toast.error("Storno fehlgeschlagen – bitte erneut versuchen.");
    } finally { setBusy(null); }
  };

  const sendReminder = async (b) => {
    if (!window.confirm(`Freundliche Zahlungserinnerung an ${b.first_name} ${b.last_name} senden?`)) return;
    setBusy(b.id);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/payment-reminder`);
      toast.success(data.message || "Erinnerung gesendet");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const toggleException = async (b) => {
    const on = !b.payment_reminder_week_before;
    setBusy(b.id);
    try {
      await api.put(`/admin/bookings/${b.id}/payment-exception`, { enabled: on });
      toast.success(on
        ? "Ausnahme aktiv: nur 1 Erinnerung 7 Tage vor Kursbeginn"
        : "Ausnahme deaktiviert: Standard-Workflow gilt wieder");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const downloadInvoice = async (b) => {
    setBusy(b.id);
    try {
      const res = await api.get(`/admin/bookings/${b.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Rechnung-${b.invoice_number || b.booking_number}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Für diese Buchung ist keine Rechnung vorhanden" : "Download fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const courseNames = [...new Set(list.map((b) => b.course_name).filter(Boolean))].sort();

  const visible = list.filter((b) => {
    const pk = payInfo(b).key;
    if (f.course && b.course_name !== f.course) return false;
    if (f.pay && pk !== f.pay) return false;
    if (f.book && (b.status || "") !== f.book) return false;
    if (f.provider && (b.payment_method || "") !== f.provider) return false;
    if (f.source && (b.booking_source || "") !== f.source) return false;
    if (f.from && (b.start || "") < f.from) return false;
    if (f.to && (b.start || "") > f.to + "T23:59:59") return false;
    if (f.q) { const s = `${b.first_name} ${b.last_name} ${b.email} ${b.booking_number}`.toLowerCase(); if (!s.includes(f.q.toLowerCase())) return false; }
    return true;
  });

  // KPIs (über alle Buchungen)
  const kpi = {
    open: list.filter((b) => ["pending", "requested"].includes(payInfo(b).key)).length,
    today: list.filter((b) => b.payment_status === "paid" && isToday(b.paid_at)).length,
    requests: list.filter((b) => payInfo(b).key === "requested").length,
    failed: list.filter((b) => ["failed", "refund_failed"].includes(payInfo(b).key)).length,
    cancelled: list.filter((b) => b.status === "cancelled").length,
    refunded: list.filter((b) => b.payment_status === "refunded").length,
    reservedSeats: list.filter((b) => ["pending", "requested"].includes(payInfo(b).key)).reduce((s, b) => s + (b.persons || 0), 0),
    paidSeats: list.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (b.persons || 0), 0),
  };
  const revenue = list.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (b.amount || 0), 0);

  const COLS = [
    { key: "booking_number", label: "Nr" },
    { key: "course_name", label: "Kurs" },
    { key: "customer", label: "Kunde", accessor: (b) => `${b.first_name} ${b.last_name}` },
    { key: "booking_source", label: "Quelle" },
    { key: "persons", label: "Pers" },
    { key: "amount", label: "Betrag" },
    { key: "payment_status", label: "Zahlung" },
    { key: "start", label: "Termin" },
  ];

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const sorted = [...visible].sort((a, b) => {
    const col = COLS.find((c) => c.key === sort.key);
    const av = col?.accessor ? col.accessor(a) : a[sort.key];
    const bv = col?.accessor ? col.accessor(b) : b[sort.key];
    let cmp;
    if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
    else cmp = String(av ?? "").localeCompare(String(bv ?? ""), "de", { numeric: true });
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const exportCsv = () => {
    const rows = [["Buchungsnr", "Kurs", "Name", "Email", "Telefon", "Quelle", "Personen", "Betrag", "Zahlung", "Status", "Datum"],
      ...visible.map((b) => [b.booking_number, b.course_name, `${b.first_name} ${b.last_name}`, b.email, b.phone, SOURCE_LABEL[b.booking_source] || b.booking_source || "", b.persons, b.amount, payInfo(b).label, b.status, b.start])];
    const csv = rows.map((r) => r.map((x) => `"${x}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "buchungen.csv"; a.click();
  };

  const selCls = "bg-[#1A1A1A] border border-white/10 text-white text-xs p-2 outline-none focus:border-[#ff0033]";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-4xl uppercase tracking-tight text-white">Buchungen & Zahlungen</h1>
        <button onClick={exportCsv} data-testid="export-csv" className="bg-[#ff0033] text-white font-mono-lab text-xs uppercase tracking-widest px-4 py-3">Export CSV</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card label="Umsatz (bezahlt)" value={euro(revenue)} />
        <Card label="Offene Zahlungen" value={kpi.open} />
        <Card label="Heute eingegangen" value={kpi.today} />
        <Card label="Offene Aufforderungen" value={kpi.requests} />
        <Card label="Fehlgeschlagen" value={kpi.failed} />
        <Card label="Stornierungen" value={kpi.cancelled} />
        <Card label="Rückerstattungen" value={kpi.refunded} />
        <Card label="Plätze bezahlt / reserviert" value={`${kpi.paidSeats} / ${kpi.reservedSeats}`} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3 bg-[#0F0F0F] border border-white/10 p-3" data-testid="booking-filter-bar">
        <input value={f.q} onChange={(e) => upd("q", e.target.value)} placeholder="Kunde / E-Mail / Nr" data-testid="filter-query" className={selCls + " w-44"} />
        <select value={f.course} onChange={(e) => upd("course", e.target.value)} data-testid="filter-course" className={selCls}><option value="">Alle Kurse</option>{courseNames.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={f.pay} onChange={(e) => upd("pay", e.target.value)} data-testid="filter-pay" className={selCls}><option value="">Alle Zahlungsstatus</option>{PAY_FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select value={f.book} onChange={(e) => upd("book", e.target.value)} data-testid="filter-book" className={selCls}><option value="">Alle Buchungsstatus</option><option value="confirmed">Bestätigt</option><option value="pending_payment">Zahlung offen</option><option value="cancelled">Storniert</option></select>
        <select value={f.provider} onChange={(e) => upd("provider", e.target.value)} data-testid="filter-provider" className={selCls}><option value="">Alle Anbieter</option><option value="stripe">Stripe</option><option value="paypal">PayPal</option><option value="free">Kostenlos</option></select>
        <select value={f.source} onChange={(e) => upd("source", e.target.value)} data-testid="filter-source" className={selCls}><option value="">Alle Quellen</option>{Object.entries(SOURCE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <input type="date" value={f.from} onChange={(e) => upd("from", e.target.value)} data-testid="filter-from" className={selCls} />
        <input type="date" value={f.to} onChange={(e) => upd("to", e.target.value)} data-testid="filter-to" className={selCls} />
        <button onClick={() => setF({ course: "", pay: "", book: "", provider: "", source: "", from: "", to: "", q: "" })} className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#ff0033] px-2">Zurücksetzen</button>
        <span className="ml-auto font-mono-lab text-[10px] text-neutral-500 uppercase tracking-widest">{visible.length} Treffer</span>
      </div>

      <div className="overflow-x-auto bg-[#0F0F0F] border border-white/10">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest">
            {COLS.map((c) => (
              <th key={c.key} onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`} className={`text-left p-3 cursor-pointer select-none hover:text-white transition-colors ${sort.key === c.key ? "text-[#ff0033]" : ""}`}>
                <span className="inline-flex items-center gap-1">{c.label}{sort.key === c.key && (sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}</span>
              </th>
            ))}
            <th className="text-left p-3">Aktion</th>
          </tr></thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-neutral-500">Keine Buchungen für diese Filter.</td></tr>}
            {sorted.map((b) => {
              const pi = payInfo(b);
              return (
              <tr key={b.id} className="border-b border-white/5 align-top" data-testid={`booking-row-${b.booking_number}`}>
                <td className="p-3 text-[#ff0033] font-mono-lab text-xs">{b.booking_number}</td>
                <td className="p-3 text-white">{b.course_name}</td>
                <td className="p-3 text-neutral-300">{b.first_name} {b.last_name}<br /><span className="text-neutral-500 text-xs">{b.email}</span>{b.phone && <><br /><span className="text-neutral-500 text-xs">{b.phone}</span></>}</td>
                <td className="p-3 text-neutral-400 text-xs">{SOURCE_LABEL[b.booking_source] || b.booking_source || "–"}</td>
                <td className="p-3 text-neutral-300">{b.persons}</td>
                <td className="p-3 text-white">{euro(b.amount)}</td>
                <td className="p-3">
                  <span className={`inline-block font-mono-lab text-[10px] uppercase tracking-wide px-2 py-1 border ${pi.cls}`} data-testid={`pay-badge-${b.booking_number}`}>{pi.label}</span>
                </td>
                <td className="p-3 text-neutral-400 text-xs">{fmtDateTime(b.start)}</td>
                <td className="p-3">
                  <div className="flex flex-col gap-1.5">
                    {b.invoice_number && (
                      <button onClick={() => downloadInvoice(b)} disabled={busy === b.id} data-testid={`invoice-download-${b.booking_number}`} title={`Rechnung ${b.invoice_number} herunterladen`} className="inline-flex items-center gap-1 font-mono-lab text-[10px] uppercase tracking-widest text-neutral-300 hover:text-[#ff0033] text-left disabled:opacity-40 transition-colors"><FileText size={12} /> Rechnung</button>
                    )}
                    {b.status !== "cancelled" ? (
                      <>
                        <ManualPaymentAction booking={b} onSaved={load} />
                        {["pending", "requested", "failed", "partial"].includes(pi.key) && <button onClick={() => sendReminder(b)} disabled={busy === b.id} data-testid={`remind-${b.booking_number}`} className="font-mono-lab text-[10px] uppercase tracking-widest text-sky-400 hover:text-sky-300 text-left disabled:opacity-40">✉ Erinnerung</button>}
                        {["pending", "requested", "failed", "partial"].includes(pi.key) && (
                          <button onClick={() => toggleException(b)} disabled={busy === b.id} data-testid={`exception-toggle-${b.booking_number}`}
                            title="Statt Standard-Workflow (Erinnerung/Mahnung/Platzfreigabe): genau eine Erinnerung 7 Tage vor Kursbeginn"
                            className={`font-mono-lab text-[10px] uppercase tracking-widest text-left disabled:opacity-40 ${b.payment_reminder_week_before ? "text-amber-400 hover:text-amber-300" : "text-neutral-500 hover:text-amber-400"}`}>
                            {b.payment_reminder_week_before ? "⏰ Ausnahme aktiv: 1× 7T vor Kurs" : "⏰ Nur 1 Erinnerung (7T vor Kurs)"}
                          </button>
                        )}
                        <button onClick={() => cancelBooking(b)} disabled={busy === b.id} data-testid={`storno-btn-${b.booking_number}`} className="inline-flex items-center gap-1 border border-[#ff0033]/40 text-[#ff0033] font-mono-lab text-[10px] uppercase tracking-widest px-2.5 py-1.5 hover:bg-[#ff0033]/10 disabled:opacity-40 transition-colors"><RotateCcw size={12} /> Storno</button>
                      </>
                    ) : (!b.invoice_number && <span className="text-xs text-neutral-500">–</span>)}
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {cancelTarget && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="cancel-modal" onClick={() => setCancelTarget(null)}>
          <div className="bg-[#111] border border-white/10 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl uppercase text-white mb-1">Buchung stornieren</h3>
            <p className="text-neutral-400 text-sm mb-1">{cancelTarget.booking_number} · {cancelTarget.first_name} {cancelTarget.last_name}</p>
            {cancelTarget.payment_status === "paid" && (
              <p className="text-[11px] text-amber-400 mb-3">Die Zahlung wird automatisch erstattet und eine Stornorechnung (Gutschrift) erstellt.</p>
            )}
            <p className="text-neutral-300 text-sm mt-3 mb-4">Soll der Kunde über die Stornierung informiert werden?</p>
            <div className="space-y-2.5">
              <button onClick={() => doCancel(true)} data-testid="cancel-notify-btn" className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3 hover:bg-[#cc0029] transition-colors">
                Mit Benachrichtigung stornieren
              </button>
              <button onClick={() => doCancel(false)} data-testid="cancel-silent-btn" className="w-full border border-white/20 text-white font-mono-lab text-xs uppercase tracking-widest py-3 hover:bg-white/5 transition-colors">
                Still stornieren (ohne E-Mail)
              </button>
              <button onClick={() => setCancelTarget(null)} data-testid="cancel-abort-btn" className="w-full text-neutral-500 text-xs py-2 hover:text-white transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
